var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Card, Stack, Skeleton, FormControl, Select, Option, IconButton, Dropdown, Menu, MenuButton, MenuItem, Button, } from "@mui/joy";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MoreVert from "@mui/icons-material/MoreVert";
import ImpersonateIcon from "@mui/icons-material/Person";
import DownloadIcon from "@mui/icons-material/Download";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useColorScheme } from "@mui/joy/styles";
import { Navigate, useNavigate } from "react-router-dom";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, } from "chart.js";
import { Line } from "react-chartjs-2";
import { useAdminCostDashboard, } from "../../data/hooks/useAdminCostDashboard";
import { useImpersonation } from "../../contexts/ImpersonationContext";
import { useAdminAccess } from "../../hooks/useAdminAccess";
import { FilterEnums } from "../../types/enums";
import { formatCurrency, formatDuration } from "../../utils";
import "./styles/styles.css";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);
var ActionDropdown = function (_a) {
    var workspaceId = _a.workspaceId, workspaceName = _a.workspaceName, onImpersonate = _a.onImpersonate;
    return (_jsxs(Dropdown, { children: [_jsx(MenuButton, __assign({ slots: { root: IconButton }, slotProps: { root: { variant: "plain", color: "neutral", size: "small" } } }, { children: _jsx(MoreVert, {}) })), _jsx(Menu, __assign({ placement: "bottom-end" }, { children: _jsxs(MenuItem, __assign({ onClick: function () { return onImpersonate(workspaceId, workspaceName); } }, { children: [_jsx(ImpersonateIcon, { sx: { marginRight: 1 } }), "Impersonate & zoom in"] })) }))] }));
};
var escapeCsv = function (val) {
    var s = val == null ? "" : String(val);
    if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
        return "\"".concat(s.replace(/"/g, '""'), "\"");
    }
    return s;
};
export var AdminCostDashboard = function () {
    var _a;
    var mode = useColorScheme().mode;
    var _b = useState(mode === "dark"), darkMode = _b[0], setDarkMode = _b[1];
    var navigate = useNavigate();
    var _c = useAdminAccess(), isAdmin = _c.isAdmin, isAdminLoading = _c.loading;
    var _d = useAdminCostDashboard(), adminSummary = _d.adminSummary, isLoadingAdminSummary = _d.isLoadingAdminSummary, getAdminSummary = _d.getAdminSummary, adminWorkspaces = _d.adminWorkspaces, getAdminWorkspaces = _d.getAdminWorkspaces;
    var startWorkspaceImpersonation = useImpersonation().startWorkspaceImpersonation;
    // Filters
    var _f = useState(FilterEnums.last7Days), filter = _f[0], setDateRange = _f[1];
    var _g = useState(null), customStartDate = _g[0], setCustomStartDate = _g[1];
    var _h = useState(null), customEndDate = _h[0], setCustomEndDate = _h[1];
    var _j = useState(false), dateRangePickerVisible = _j[0], setShowDateRangePicker = _j[1];
    var dateRangeRef = useRef(null);
    var _k = useState([
        { startDate: new Date(), endDate: new Date(), key: "selection" },
    ]), selectedRange = _k[0], setSelectedRange = _k[1];
    var _l = useState(""), selectedWorkspace = _l[0], setSelectedWorkspace = _l[1];
    var displayRange = function () {
        if (!customStartDate || !customEndDate)
            return "Custom Range";
        return "".concat(customStartDate.toLocaleDateString(), " - ").concat(customEndDate.toLocaleDateString());
    };
    useEffect(function () {
        setDarkMode(mode === "dark");
    }, [mode]);
    useEffect(function () {
        if (!isAdmin)
            return;
        getAdminWorkspaces();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);
    useEffect(function () {
        if (!isAdmin)
            return;
        getAdminSummary(filter, customStartDate, customEndDate, selectedWorkspace || undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, filter, customStartDate, customEndDate, selectedWorkspace]);
    // Outside click closes date picker
    useEffect(function () {
        function handleClickOutside(event) {
            if (dateRangeRef.current &&
                !dateRangeRef.current.contains(event.target)) {
                setShowDateRangePicker(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return function () { return document.removeEventListener("mousedown", handleClickOutside); };
    }, []);
    var handleImpersonate = useCallback(function (workspaceId, workspaceName) {
        // Reuses the existing impersonation flow — after reload the user lands on cost-dashboard
        // with mwiid header set for all requests.
        sessionStorage.setItem("dw_post_impersonate_redirect", "/cost-dashboard");
        startWorkspaceImpersonation(workspaceId, workspaceName);
    }, [startWorkspaceImpersonation]);
    // Redirect after impersonation page reload
    useEffect(function () {
        var target = sessionStorage.getItem("dw_post_impersonate_redirect");
        if (target) {
            sessionStorage.removeItem("dw_post_impersonate_redirect");
            navigate(target);
        }
    }, [navigate]);
    var defaultColDef = useMemo(function () { return ({ sortable: true, resizable: true, filter: true }); }, []);
    var columnDefs = useMemo(function () { return [
        {
            headerName: "Workspace",
            field: "workspaceName",
            flex: 2,
            minWidth: 180,
        },
        {
            headerName: "Owner",
            field: "ownerEmail",
            flex: 2,
            minWidth: 180,
            valueFormatter: function (p) { return p.value || "N/A"; },
        },
        {
            headerName: "Calls",
            field: "totalCalls",
            flex: 1,
            minWidth: 90,
        },
        {
            headerName: "Billable Time",
            field: "totalBillableMinutes",
            flex: 1,
            minWidth: 120,
            valueFormatter: function (p) { return formatDuration(p.value || 0); },
        },
        {
            headerName: "Total Cost",
            field: "totalCost",
            flex: 1,
            minWidth: 110,
            sort: "desc",
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "Avg Cost / Call",
            field: "averageCostPerCall",
            flex: 1,
            minWidth: 130,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "Margin",
            field: "totalMargin",
            flex: 1,
            minWidth: 110,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "LLM",
            field: "totalLlmCost",
            flex: 1,
            minWidth: 90,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "TTS",
            field: "totalTtsCost",
            flex: 1,
            minWidth: 90,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "STT",
            field: "totalSttCost",
            flex: 1,
            minWidth: 90,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "Telephony",
            field: "totalTelephonyCost",
            flex: 1,
            minWidth: 110,
            valueFormatter: function (p) { return formatCurrency(p.value || 0); },
        },
        {
            headerName: "Actions",
            field: "workspaceId",
            sortable: false,
            filter: false,
            width: 100,
            maxWidth: 100,
            cellRenderer: function (params) { return (_jsx(Box, __assign({ sx: {
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    justifyContent: "center",
                } }, { children: _jsx(ActionDropdown, { workspaceId: params.data.workspaceId, workspaceName: params.data.workspaceName, onImpersonate: handleImpersonate }) }))); },
        },
    ]; }, [handleImpersonate]);
    var handleExportCSV = function () {
        var _a;
        if (!((_a = adminSummary === null || adminSummary === void 0 ? void 0 : adminSummary.workspaces) === null || _a === void 0 ? void 0 : _a.length))
            return;
        var rows = adminSummary.workspaces;
        var header = [
            "Workspace",
            "Owner",
            "External Workspace ID",
            "Calls",
            "Billable Minutes",
            "Total Cost",
            "Avg Cost Per Call",
            "Margin",
            "LLM Cost",
            "TTS Cost",
            "STT Cost",
            "Telephony Cost",
            "Transfer Telephony Cost",
        ].join(",");
        var lines = rows.map(function (r) {
            var _a, _b, _c, _d, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
            return [
                escapeCsv(r.workspaceName),
                escapeCsv(r.ownerEmail || ""),
                escapeCsv(r.externalWorkspaceId || ""),
                escapeCsv(r.totalCalls),
                escapeCsv((_c = (_b = (_a = r.totalBillableMinutes) === null || _a === void 0 ? void 0 : _a.toFixed) === null || _b === void 0 ? void 0 : _b.call(_a, 4)) !== null && _c !== void 0 ? _c : r.totalBillableMinutes),
                escapeCsv((_g = (_f = (_d = r.totalCost) === null || _d === void 0 ? void 0 : _d.toFixed) === null || _f === void 0 ? void 0 : _f.call(_d, 4)) !== null && _g !== void 0 ? _g : r.totalCost),
                escapeCsv((_k = (_j = (_h = r.averageCostPerCall) === null || _h === void 0 ? void 0 : _h.toFixed) === null || _j === void 0 ? void 0 : _j.call(_h, 4)) !== null && _k !== void 0 ? _k : r.averageCostPerCall),
                escapeCsv((_o = (_m = (_l = r.totalMargin) === null || _l === void 0 ? void 0 : _l.toFixed) === null || _m === void 0 ? void 0 : _m.call(_l, 4)) !== null && _o !== void 0 ? _o : r.totalMargin),
                escapeCsv((_r = (_q = (_p = r.totalLlmCost) === null || _p === void 0 ? void 0 : _p.toFixed) === null || _q === void 0 ? void 0 : _q.call(_p, 4)) !== null && _r !== void 0 ? _r : r.totalLlmCost),
                escapeCsv((_u = (_t = (_s = r.totalTtsCost) === null || _s === void 0 ? void 0 : _s.toFixed) === null || _t === void 0 ? void 0 : _t.call(_s, 4)) !== null && _u !== void 0 ? _u : r.totalTtsCost),
                escapeCsv((_x = (_w = (_v = r.totalSttCost) === null || _v === void 0 ? void 0 : _v.toFixed) === null || _w === void 0 ? void 0 : _w.call(_v, 4)) !== null && _x !== void 0 ? _x : r.totalSttCost),
                escapeCsv((_0 = (_z = (_y = r.totalTelephonyCost) === null || _y === void 0 ? void 0 : _y.toFixed) === null || _z === void 0 ? void 0 : _z.call(_y, 4)) !== null && _0 !== void 0 ? _0 : r.totalTelephonyCost),
                escapeCsv((_3 = (_2 = (_1 = r.totalTransferTelephonyCost) === null || _1 === void 0 ? void 0 : _1.toFixed) === null || _2 === void 0 ? void 0 : _2.call(_1, 4)) !== null && _3 !== void 0 ? _3 : r.totalTransferTelephonyCost),
            ].join(",");
        });
        var csv = __spreadArray([header], lines, true).join("\n");
        var blob = new Blob([csv], { type: "text/csv" });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        a.href = url;
        a.download = "admin-cost-dashboard-".concat(new Date().toISOString().slice(0, 10), ".csv");
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };
    var renderSummaryCards = function () {
        if (isLoadingAdminSummary) {
            return (_jsx(Stack, __assign({ direction: "row", spacing: 2, sx: { mb: 3, flexWrap: "wrap", gap: 2 } }, { children: [1, 2, 3, 4, 5].map(function (i) { return (_jsx(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: _jsx(Skeleton, { variant: "rectangular", height: 100 }) }), i)); }) })));
        }
        var t = adminSummary === null || adminSummary === void 0 ? void 0 : adminSummary.totals;
        return (_jsxs(Stack, __assign({ direction: "row", spacing: 2, sx: { mb: 3, flexWrap: "wrap", gap: 2 } }, { children: [_jsxs(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(AttachMoneyIcon, {}), _jsx(Typography, __assign({ level: "body-sm", color: "neutral" }, { children: "Total Cost" }))] })), _jsx(Typography, __assign({ level: "h2" }, { children: formatCurrency((t === null || t === void 0 ? void 0 : t.totalCost) || 0) }))] })), _jsxs(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(TrendingUpRoundedIcon, {}), _jsx(Typography, __assign({ level: "body-sm", color: "neutral" }, { children: "Total Margin" }))] })), _jsx(Typography, __assign({ level: "h2" }, { children: formatCurrency((t === null || t === void 0 ? void 0 : t.totalMargin) || 0) }))] })), _jsxs(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(QueryStatsIcon, {}), _jsx(Typography, __assign({ level: "body-sm", color: "neutral" }, { children: "Total Calls" }))] })), _jsx(Typography, __assign({ level: "h2" }, { children: (t === null || t === void 0 ? void 0 : t.totalCalls) || 0 }))] })), _jsxs(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(SmartToyIcon, {}), _jsx(Typography, __assign({ level: "body-sm", color: "neutral" }, { children: "Total Billable Time" }))] })), _jsx(Typography, __assign({ level: "h2" }, { children: formatDuration((t === null || t === void 0 ? void 0 : t.totalBillableMinutes) || 0) }))] })), _jsxs(Card, __assign({ sx: { flex: "1 1 200px", minWidth: 180 } }, { children: [_jsxs(Stack, __assign({ direction: "row", alignItems: "center", spacing: 1 }, { children: [_jsx(GroupsRoundedIcon, {}), _jsx(Typography, __assign({ level: "body-sm", color: "neutral" }, { children: "Workspaces" }))] })), _jsx(Typography, __assign({ level: "h2" }, { children: (t === null || t === void 0 ? void 0 : t.totalWorkspaces) || 0 })), _jsxs(Typography, __assign({ level: "body-xs", color: "neutral" }, { children: ["Avg cost/call ", formatCurrency((t === null || t === void 0 ? void 0 : t.averageCostPerCall) || 0)] }))] }))] })));
    };
    var renderDailyChart = function () {
        if (isLoadingAdminSummary) {
            return (_jsxs(Card, __assign({ sx: { mb: 3 } }, { children: [_jsx(Typography, __assign({ level: "title-lg", sx: { mb: 2 } }, { children: "Daily Cost Trends" })), _jsx(Skeleton, { variant: "rectangular", height: 300 })] })));
        }
        var stats = (adminSummary === null || adminSummary === void 0 ? void 0 : adminSummary.dailyStats) || [];
        if (stats.length === 0)
            return null;
        var chartData = {
            labels: stats.map(function (s) {
                var d = new Date(s.date);
                return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            }),
            datasets: [
                {
                    label: "Daily Cost",
                    data: stats.map(function (s) { return parseFloat(Number(s.cost).toFixed(2)); }),
                    borderColor: "rgb(53, 162, 235)",
                    backgroundColor: "rgba(53, 162, 235, 0.5)",
                    tension: 0.1,
                },
                {
                    label: "Sessions",
                    data: stats.map(function (s) { return s.sessions; }),
                    borderColor: "rgb(255, 99, 132)",
                    backgroundColor: "rgba(255, 99, 132, 0.5)",
                    yAxisID: "y1",
                    tension: 0.1,
                },
            ],
        };
        var chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            plugins: {
                legend: { display: true },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            var label = context.dataset.label || "";
                            if (label)
                                label += ": ";
                            if (context.dataset.label === "Daily Cost") {
                                label += "$" + parseFloat(context.parsed.y).toFixed(2);
                            }
                            else {
                                label += context.parsed.y;
                            }
                            return label;
                        },
                    },
                },
            },
            scales: {
                x: { display: true, title: { display: true, text: "Date" } },
                y: {
                    type: "linear",
                    display: true,
                    position: "left",
                    title: { display: true, text: "Cost ($)" },
                    ticks: {
                        callback: function (value) {
                            return "$" + parseFloat(value).toFixed(2);
                        },
                    },
                },
                y1: {
                    type: "linear",
                    display: true,
                    position: "right",
                    title: { display: true, text: "Sessions" },
                    grid: { drawOnChartArea: false },
                },
            },
        };
        return (_jsxs(Card, __assign({ sx: { mb: 3 } }, { children: [_jsxs(Typography, __assign({ level: "title-lg", sx: { mb: 2 } }, { children: ["Daily Cost Trends ", selectedWorkspace ? "(Filtered)" : "(All Workspaces)"] })), _jsx(Box, __assign({ sx: { height: 300, width: "100%" } }, { children: _jsx(Line, { data: chartData, options: chartOptions }) }))] })));
    };
    // Gate: while we don't know yet, show a neutral skeleton (same as loading any other page)
    if (isAdminLoading) {
        return (_jsxs(Box, __assign({ sx: { p: 3 } }, { children: [_jsx(Skeleton, { variant: "rectangular", height: 80, sx: { mb: 2 } }), _jsx(Skeleton, { variant: "rectangular", height: 200 })] })));
    }
    // Non-admins get a silent redirect to the default home — no "access denied" message,
    // so the page's existence and gating rules aren't leaked.
    if (!isAdmin) {
        return _jsx(Navigate, { to: "/ai-agents", replace: true });
    }
    return (_jsx(Box, __assign({ sx: { overflowY: "auto" } }, { children: _jsxs(Box, __assign({ sx: { pl: 3.5, pr: 4, pt: 1, width: "100%" } }, { children: [_jsx(Typography, __assign({ level: "h1", sx: { mb: 3 } }, { children: "Admin Cost Dashboard" })), _jsx(Card, __assign({ sx: { mb: 1 } }, { children: _jsxs(Stack, __assign({ direction: "row", spacing: 2, sx: { flexWrap: "wrap" } }, { children: [_jsx(FormControl, { children: _jsxs(Select, __assign({ size: "sm", value: filter, onChange: function (_e, newVal) {
                                        if (!newVal)
                                            return;
                                        setDateRange(newVal);
                                        setShowDateRangePicker(newVal === FilterEnums.custom);
                                    }, placeholder: "Select Date Range", sx: { minWidth: 200 } }, { children: [_jsx(Option, __assign({ value: FilterEnums.last30Minutes }, { children: "Last 30 Minutes" })), _jsx(Option, __assign({ value: FilterEnums.last1Hour }, { children: "Last Hour" })), _jsx(Option, __assign({ value: FilterEnums.last4Hours }, { children: "Last 4 Hours" })), _jsx(Option, __assign({ value: FilterEnums.last12Hours }, { children: "Last 12 Hours" })), _jsx(Option, __assign({ value: FilterEnums.last24Hours }, { children: "Last 24 Hours" })), _jsx(Option, __assign({ value: FilterEnums.last48Hours }, { children: "Last 48 Hours" })), _jsx(Option, __assign({ value: FilterEnums.today }, { children: "Today" })), _jsx(Option, __assign({ value: FilterEnums.yesterday }, { children: "Yesterday" })), _jsx(Option, __assign({ value: FilterEnums.last3Days }, { children: "Last 3 Days" })), _jsx(Option, __assign({ value: FilterEnums.last7Days }, { children: "Last 7 Days" })), _jsx(Option, __assign({ value: FilterEnums.last1Month }, { children: "Last 1 Month" })), _jsx(Option, __assign({ value: FilterEnums.last3Months }, { children: "Last 3 Months" })), _jsx(Option, __assign({ value: FilterEnums.all }, { children: "All" })), _jsx(Option, __assign({ value: FilterEnums.custom, onClick: function () {
                                                setDateRange(FilterEnums.custom);
                                                setShowDateRangePicker(true);
                                            } }, { children: displayRange() }))] })) }), _jsx(FormControl, { children: _jsxs(Select, __assign({ value: selectedWorkspace, onChange: function (_, value) { return setSelectedWorkspace(value || ""); }, placeholder: "All Workspaces", size: "sm", sx: { minWidth: 250 } }, { children: [_jsx(Option, __assign({ value: "" }, { children: "All Workspaces" })), adminWorkspaces.map(function (w) { return (_jsxs(Option, __assign({ value: w.workspaceId }, { children: [w.workspaceName, w.ownerEmail ? " \u2014 ".concat(w.ownerEmail) : ""] }), w.workspaceId)); })] })) })] })) })), filter === FilterEnums.custom && dateRangePickerVisible && (_jsx(Box, __assign({ ref: dateRangeRef, sx: { position: "absolute", zIndex: 99999, display: "inline-block" } }, { children: _jsx(DateRange, { ranges: selectedRange, onChange: function (item) {
                            setSelectedRange([item.selection]);
                            setCustomStartDate(item.selection.startDate);
                            setCustomEndDate(item.selection.endDate);
                        }, moveRangeOnFirstSelection: false, editableDateInputs: true, className: mode === "dark" ? "custom-date-range-picker" : "" }) }))), renderSummaryCards(), renderDailyChart(), _jsxs(Card, { children: [_jsxs(Box, __assign({ sx: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            } }, { children: [_jsx(Typography, __assign({ level: "title-lg" }, { children: "Workspace Cost Breakdown" })), _jsx(Button, __assign({ variant: "outlined", startDecorator: _jsx(DownloadIcon, {}), onClick: handleExportCSV, disabled: !((_a = adminSummary === null || adminSummary === void 0 ? void 0 : adminSummary.workspaces) === null || _a === void 0 ? void 0 : _a.length) }, { children: "Export CSV" }))] })), _jsx(Box, __assign({ sx: { width: "100%", height: 600 }, className: darkMode ? "ag-theme-quartz-dark" : "ag-theme-quartz" }, { children: _jsx(AgGridReact, { rowData: (adminSummary === null || adminSummary === void 0 ? void 0 : adminSummary.workspaces) || [], columnDefs: columnDefs, defaultColDef: defaultColDef, pagination: true, paginationPageSize: 50, enableCellTextSelection: true, animateRows: true, suppressCellFocus: true, ensureDomOrder: true }) }))] })] })) })));
};
